<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, nextTick } from "vue";
import { useRouter } from "vue-router";
import { FakeLiveInfo } from "@/types";
import {userUserStore} from "@/store/user.ts";
import {MessageService, SectionService} from "@/api/api.ts";
import {ElNotification} from "element-plus";
import {Loading} from "@element-plus/icons-vue";

const userStore = userUserStore();

// 用户页码
let uPage = 1;
// 用户页大小
const uPageSize = 15;
// 历史消息页码
let msgPage: any = null;
// 历史消息多少
const msgPageSize = 20;

const msgTime = ref(0);
const isCompleted = ref(false);
const isLoadingHistory = ref(false);

const activeKey = ref<number>(0);
const text = ref<string>("");
const router = useRouter();
const sectionId = ref<number>();
const fakeClassDetail = ref<FakeLiveInfo>();
const noMoreMember = ref<boolean>(false);
const timer = ref<NodeJS.Timeout>();
const newTimer = ref<NodeJS.Timeout>();

const showNewTip = ref<boolean>(false);
const showEndTip = ref<boolean>(false);
const msgList = ref<HTMLDivElement>();
const memberListRef = ref<HTMLDivElement>();

// 是否全体禁言
const muted = ref<boolean>(false);

// 直播间消息列表
const messageList = ref<
    {
      id: number;
      user_name: string;
      message: string;
      role: number;
      avatar?: string;
    }[]
>([]);

// 群成员列表
const memberList = ref<{
  id: number;
  user_name: string;
  avatar: string;
}[]>([]);

// 获取群成员数
const memberCount = ref<number>(0);

const playerOptions = {
  id: "player-container",
  source: "",
  width: "",
  height: "",
  // 直播
  isLive: true,
  playsinline: true,
  autoplay: true,
  useH5Prism: true,
  waitingTimeout: 10,
  controlBarVisibility: "hover",
  skinLayout: [
    {
      name: "H5Loading",
      align: "cc",
    },
    {
      name: "errorDisplay",
      align: "tlabs",
      x: 0,
      y: 0,
    },
    {
      name: "infoDisplay",
    },
    {
      name: "controlBar",
      align: "blabs",
      x: 0,
      y: 0,
      children: [
        {
          name: "liveDisplay",
          align: "tlabs",
          x: 15,
          y: 6,
        },
        {
          name: "fullScreenButton",
          align: "tr",
          x: 10,
          y: 10,
        },
        {
          name: "volume",
          align: "tr",
          x: 5,
          y: 10,
        },
      ],
    },
  ],
};

const createPlayer = (playerOptions: any): any => {
  const player = new Aliplayer(playerOptions, player => {
    player.mute();
  });
  player.on("ended", () => {
    console.log("ended...");
    showEndTip.value = true;
  });
  player.on("error", (error: any) => {
    //隐藏
    const element = document.getElementsByClassName("prism-ErrorMessage")[0] as HTMLDivElement;
    element.style.display = "none";
    //解析
    const errorData = error.paramData;
    console.log(errorData);
    if (errorData.error_code === 4002 || errorData.error_code === 4008) {
      // 显示播放已结束
      showEndTip.value = true;
    }
  });
  return player;
};

const getLiveInfo = async () => {
  const res = await SectionService.startLiveClass({
    id: sectionId.value || 0,
  });
  if (res.data.code === 200) {
    muted.value = res.data.data?.is_banned === 1;
    fakeClassDetail.value = res.data.data;
    playerOptions.source = res.data.data?.url || '';
    createPlayer(playerOptions);
    getHistoryMessage(true);
    getMemberList();
    loopGetMember();
  } else {
    ElNotification.error({
      message: res.data.msg,
    });
    // router.back();
  }
};

const getHistoryMessage = async (firstTime = false) => {
  try {
    isLoadingHistory.value = true;
    const res = await MessageService.getGroupMessageList({
      id: fakeClassDetail.value?.section_id || 0,
      page: msgPage,
      pageSize: msgPageSize,
    });
    if (res.data.code === 200 && res.data.data) {
      msgPage = res.data.data.page;
      if (msgPage <= 1) {
        isCompleted.value = true;
      } else {
        isCompleted.value = false;
      }
      if (msgPage > 1) {
        msgPage--;
      }
      // memberCount.value = res.data.data.member_count;
      const messages = [];
      if (firstTime) {
        messages.push({
          id: -2,
          user_name: ``,
          message: `欢迎来到直播间！严禁出现违法违规发言信息，视违反情节轻重追究责任。`,
          role: -2,
        });
        if (res.data.data.list.length > 0) {
          msgTime.value = res.data.data.list[res.data.data.list.length - 1].create_time;
        }
      }
      for (let i = 0; i < res.data.data.list.length; i++) {
        const message = res.data.data.list[i];
        messages.push({
          id: message.id,
          user_name: message.user_name,
          message: message.msg_body,
          // TODO 用户的身份
          role: 0,
        });
      }
      if (messages.length > 0) {
        // @ts-ignore
        messageList.value.unshift(...messages);
      }
      if (firstTime) {
        loopGetNewMessage();
      }
    }
  } catch (err) {
    console.warn(err);
  } finally {
    isLoadingHistory.value = false;
  }
};

const getNewMessage = async () => {
  try {
    const res = await MessageService.findGroupChat({
      id: fakeClassDetail.value?.section_id || 0,
      msg_time: msgTime.value,
    });
    if (res.data.code === 200 && res.data.data) {
      // memberCount.value = res.data.data.member_count;
      const messages = [];
      if (res.data.data.list.length > 0) {
        msgTime.value = res.data.data.list[res.data.data.list.length - 1].create_time;
      }
      for (let i = 0; i < res.data.data.list.length; i++) {
        const message = res.data.data.list[i];
        messages.push({
          id: message.id,
          user_name: message.user_name,
          message: message.msg_body,
          // TODO 消息身份
          role: message.user_id === 0,
        });
      }
      let result = messages.filter(item => {
        return !messageList.value.find(prop => {
          return prop.id === item.id;
        });
      });
      if (result.length > 0) {
        // @ts-ignore
        messageList.value.push(...result);
        if (msgList.value) {
          showNewTip.value =
              Math.abs(
                  msgList.value.scrollTop + msgList.value.clientHeight - msgList.value.scrollHeight
              ) >= 5;
        }
        if (!showNewTip.value) {
          await nextTick(() => {
            if (msgList.value) {
              msgList.value.scrollTop = msgList.value.scrollHeight;
            }
          });
        }
      }
    }
  } catch (err) {
    console.warn(err);
  }
};

const getMoreMembers = () => {
  uPage++;
  getMemberList();
};

const getMemberList = async () => {
  try {
    const res = await MessageService.getGroupMemberList({
      id: fakeClassDetail.value?.section_id || 0,
      page: uPage,
      pageSize: uPageSize,
    });
    if (uPage >= 2) {
      if (timer.value) {
        clearInterval(timer.value);
      }
    }
    if (res.data.code === 200) {
      const members = [];
      if (uPage === 1) {
        if (res.data.data) {
          members.push({
            id: res.data.data.user_info.id || 0,
            user_name: res.data.data.user_info.user_name || "",
            avatar: res.data.data.user_info.head_img || "",
          });
          for (let i = 0; i < res.data.data.list.length; i++) {
            const member = res.data.data.list[i];
            members.push({
              id: member.id,
              user_name: member.user_name,
              avatar: member.head_img,
            });
          }
          memberList.value = members;
        }
      } else {
        if (res.data.data) {
          for (let i = 0; i < res.data.data.list.length; i++) {
            const member = res.data.data.list[i];
            members.push({
              id: member.id,
              user_name: member.user_name,
              avatar: member.head_img,
            });
          }
          memberList.value.push(...members);
        }
      }
      if (res.data.data) {
        memberCount.value = res.data.data.count;
        noMoreMember.value = memberList.value.length >= res.data.data.count;
      }
    }
  } catch (err) {
    console.warn(err);
  }
};

// 设置禁言
const setMute = async (mute: boolean) => {
  try {
    const res = await MessageService.banGroup({
      id: fakeClassDetail.value?.section_id || 0,
      is_banned: mute ? 1 : 0,
    });
    if (res.data.code === 200) {
      muted.value = mute;
      if (msgList.value) {
        showNewTip.value =
            Math.abs(
                msgList.value.scrollTop + msgList.value.clientHeight - msgList.value.scrollHeight
            ) >= 5;
      }
      if (muted.value) {
        messageList.value.push({
          id: -1,
          user_name: "系统消息",
          message: "老师已关闭讨论区",
          role: -1,
        });
      } else {
        messageList.value.push({
          id: -1,
          user_name: "系统消息",
          message: "老师已打开讨论区",
          role: -1,
        });
      }
      if (!showNewTip.value) {
        await nextTick(() => {
          if (msgList.value) {
            msgList.value.scrollTop = msgList.value.scrollHeight;
          }
        });
      }
    }
  } catch (err) {
    console.warn(err);
  }
};

const loopGetNewMessage = () => {
  if (newTimer.value) {
    clearInterval(newTimer.value);
  }
  // 5s获取一次新消息
  newTimer.value = setInterval(() => {
    getNewMessage();
  }, 5000);
};

const loopGetMember = () => {
  if (timer.value) {
    clearInterval(timer.value);
  }
  // 20s获取一次
  timer.value = setInterval(() => {
    uPage = 1;
    getMemberList();
  }, 20000);
};

onMounted(() => {
  sectionId.value = parseInt(router.currentRoute.value.params.id as string);
  if (msgList.value) {
    msgList.value.onscroll = () => {
      if (msgList.value) {
        if (
            Math.abs(msgList.value.scrollTop + msgList.value.clientHeight - msgList.value.scrollHeight) <
            5
        ) {
          showNewTip.value = false;
        }
      }
    };
  }
  if (memberListRef.value) {
    memberListRef.value.onscroll = () => {
      if (memberListRef.value) {
        if (Math.abs(memberListRef.value.scrollTop) < 5) {
          if (!timer.value) {
            loopGetMember();
          }
        }
      }
    };
  }
  getLiveInfo();
});

onBeforeUnmount(() => {
  if (timer.value) {
    clearInterval(timer.value);
  }
  if (newTimer.value) {
    clearInterval(newTimer.value);
  }
});

const onMessageSend = async () => {
  if (!text.value) {
    return ElNotification.warning({
      message: "请输入内容",
    });
  }
  try {
    const res = await MessageService.addGroupMessage({
      id: fakeClassDetail.value?.section_id || 0,
      msg_body: text.value,
    });
    if (res.data.code === 200) {
      const message = {
        id: Number(res.data.data?.msg_id),
        user_name: userStore.name,
        message: res.data.data?.msg_body || '',
        role: 1,
      };
      const item = messageList.value.find(item => item.id === message.id);
      if (!item) {
        messageList.value.push(message);
      }
      text.value = "";
      await nextTick(() => {
        if (msgList.value) {
          msgList.value.scrollTop = msgList.value.scrollHeight;
        }
      });
    } else {
      ElNotification.error({
        title: "消息发送失败",
        message: res.data.msg,
      })
    }
  } catch (err) {
    console.warn(err);
  }
};

const keydown = (e: KeyboardEvent) => {
  console.log(e.code);
  if (!e.shiftKey && e.code === "Enter") {
    e.stopPropagation(); //Firefox阻止冒泡行为
    e.preventDefault(); //取消事件的默认动作*换行
    //以下处理发送消息代码
    onMessageSend();
  }
};

const onActiveKeyChange = (key: number) => {
  activeKey.value = key;
};
</script>

<template>
  <div class="mx-7.5rem mt-5rem h-80% flex items-center justify-center py-1rem">
    <div
        class="
        flex
        items-center
        w-1440px
        rounded-4px
        bg-white
        pt-[20px]
        pr-[20px]
        pb-[40px]
        pl-[40px]
        h-100%
      "
    >
      <div id="player-container" class="w-[1050px] h-100% relative">
        <div
            class="border-[#3ec75a] text-white absolute left-[calc(50%-40px)] top-[50%]"
            ref="myErrorContainer"
            v-if="showEndTip"
        >
          直播已结束
        </div>
      </div>
      <div class="w-[302px] h-100% flex flex-col bg-[#f6f7f9] ml-[31px]">
        <div class="flex">
          <div class="flex-1 flex justify-center py-10px bg-#e6e6e6 cursor-pointer" :class="activeKey === 0 ? `bg-#f6f7f9 border-2px border-transparent text-#FF6600CC border-t-#FF6600CC border-solid ` : ``" @click="onActiveKeyChange(0)">
            讨论区
          </div>
          <div class="flex-1 flex justify-center py-10px bg-#e6e6e6 cursor-pointer" :class="activeKey === 1 ? `bg-#f6f7f9 border-2px border-transparent text-#FF6600CC border-t-#FF6600CC border-solid  ` : ``" @click="onActiveKeyChange(1)">
            {{ `成员(${memberCount})` }}
          </div>
        </div>
        <div class="flex-1 h-0 flex flex-col" v-show="activeKey === 0">
          <div class="flex-1 h-0">
            <div class="h-full overflow-y-auto" ref="msgList">
              <div
                  class="flex justify-center ml-auto mr-auto mt-[16px] cursor-pointer"
                  v-if="!isCompleted && !isLoadingHistory"
                  @click="getHistoryMessage(false)"
              >
                获取更多历史消息...
              </div>
              <div class="flex justify-center mx-auto mt-[16px]" v-if="isLoadingHistory">
                <el-icon class="animate-spin">
                  <Loading />
                </el-icon>
              </div>
              <div class="flex items-center pt-[16px]" v-for="item in messageList">
                <div class="text-[16px] text-[rgba(0,0,0,0.65)] px-16px">
                  <div v-if="item.role === -2" class="bg-white p-8px mr-4px rounded-8px font-bold">
                    <div class="flex items-center">
                      <img src="@/assets/image/icon/icon-broadcast.png" class="w-16px h-16px text-#666"  alt="微同课"/>
                      <span class="ml-4px">微同课提示：</span>
                    </div>
                    {{ item.message }}
                  </div>
                  <span v-else>
                    <span v-if="item.role === 1" style="color: #ffc000">
                      <img src="@/assets/image/icon/icon-auth.png" class="w-16px h-16px text-#666 mr-1px"  alt="微同课"/>
                      老师(我)
                    </span>
                    <span v-else-if="item.role === 0">{{ item.user_name }}</span>
                    <span v-else-if="item.role === -1">{{ item.user_name }}</span>
                    :{{ item.message }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div class="flex items-center p-16px relative">
            <div
                class="
                flex
                justify-center
                bg-white
                rounded-[12px]
                absolute
                top-[-30px]
                left-[36%]
                pt-[8px]
                pr-[16px]
                pb-[8px]
                pl-[16px]
                select-none
                cursor-pointer
              "
                v-show="showNewTip"
            >
              底部有新消息
            </div>
            <img
                src="@/assets/image/icon/chat-disabled.png"
                v-show="muted"
                @click="setMute(false)"
                class="cursor-pointer"
                alt="微同课"
            />
            <img
                src="@/assets/image/icon/chat-enable.png"
                v-show="!muted"
                @click="setMute(true)"
                class="cursor-pointer"
                alt="微同课"
            />
            <el-input
                type="textarea"
                class="message-box rounded"
                v-model="text"
                @keydown="keydown"
                style="height: 50px; border-radius: 25px"
                placeholder="我也说两句"
                :autosize="{ minRows: 1, maxRows: 1 }"
            />
          </div>
        </div>
        <div
            class="member-active h-full overflow-y-auto"
            v-show="activeKey === 1"
            ref="memberListRef"
        >
          <div class="mt-[16px] ml-[20px]" v-for="item in memberList">
            <img :src="item.avatar" class="w-[32px] h-[32px] object-cover rounded-[50%]"  alt="头像"/>
            <span class="ml-[16px]">{{ item.user_name }}</span>
          </div>
          <div
              class="mt-[16px] flex justify-center cursor-pointer"
              v-if="!noMoreMember"
              @click="getMoreMembers"
          >
            加载更多
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
